import { useQuery, NetworkStatus } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  CursorDirection,
  SubscriberLegalMatterListingDocument,
} from '@generatedTypes/graphql'

import { routes } from '@/routes'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import Link from '@mui/material/Link'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import LegalMatterListItem from '@components/organisms/LegalMatterListItem'
import { LegalMatterListLoading } from '@components/molecules/LoadingSkeletons'
import LegalMatterFilters from '@components/molecules/LegalMatterFilters'
import ErrorScreen from '@components/organisms/ErrorScreen'
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp'
import {
  LegalMatterFilterType,
  SubscriberLegalMatterStatusLabels,
  UserFilters,
} from '@utils/StatusMapper'
import SubscriberUser from '@models/SubscriberUser'
import { paginationLimits } from '@/config'
import Box from '@mui/material/Box'
import { useLegalMatterFilters } from '@utils/useLegalMatterFilters'
import useCurrentUser from '@utils/useCurrentUser'
import { useUSStateDatabase } from '@utils/useUSStateDatabase'

const SubscriberLegalMatterListing = (): JSX.Element => {
  const { user, loading: userLoading, updateUserPreferences } = useCurrentUser()
  const preferredStates = user?.preferences?.listingFilters?.states ?? []
  const { states: usStates } = useUSStateDatabase({
    operationalStatesOnly: true,
  })
  const hasSubscription: boolean =
    user instanceof SubscriberUser ? user.hasAnActiveSubscription() : false
  const hasPastdueSubscription: boolean =
    user instanceof SubscriberUser ? user.hasPastDueSubscription() : false
  const navigate = useNavigate()

  const { filters, filterName, statusMapper, stateCodes, persistFilterState } =
    useLegalMatterFilters(LegalMatterFilterType.SUBSCRIBER, user)

  const handleFilterUpdates = (userFilters: UserFilters) => {
    if (userFilters?.usStates != null) {
      updateUserPreferences({
        variables: {
          userKey: user?.key,
          data: { listing_filters: { states: userFilters.usStates } },
        },
      })
    }

    persistFilterState(userFilters)
  }

  const { data, networkStatus, error, fetchMore } = useQuery(
    SubscriberLegalMatterListingDocument,
    {
      skip: user?.key == null,
      notifyOnNetworkStatusChange: true,
      variables: {
        ...filters,
        subscriberRef: user?.key ?? null,
        limit: paginationLimits.legalMatters,
        cursor: null,
        stateCodes: stateCodes,
        cursorDirection: CursorDirection.Next,
        calendarEventsLimit: paginationLimits.calendarEvents,
        calendarEventsCursor: null,
        calendarEventsCursorDirection: CursorDirection.Next,
      },
    },
  )

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    userLoading
  ) {
    return <LegalMatterListLoading numberOfSkeletons={5} />
  }

  if (error !== undefined) {
    return (
      <ErrorScreen content="Sorry, we can not show results, try again later" />
    )
  }

  const onClickLoadMore = (): void => {
    if (data?.legalMatters.pageInfo.endCursor != null) {
      void fetchMore({
        variables: {
          cursor: data?.legalMatters.pageInfo.endCursor,
        },
      })
    }
  }

  const renderNoResultsMessage = (filter: string): JSX.Element => {
    let message: string
    switch (filter) {
      case 'New':
        message = 'There are no new legal matters.'
        break

      case 'Current':
        message = 'There are no current legal matters.'
        break

      case 'Past':
        message = 'There are no past legal matters.'
        break

      case 'All':
      default:
        message = 'There are no legal matters.'
        break
    }

    return (
      <>
        <Alert severity="info" icon={false}>
          <Typography gutterBottom>{message}</Typography>
        </Alert>
        {filter === 'All' && (
          <Alert severity="info" icon={false} sx={{ mt: 3 }}>
            <Typography variant="h5" gutterBottom>
              Next steps:
            </Typography>
            <ul>
              <li>
                Click on the Request Legal Service button to open a new legal
                matter
              </li>
              <li>Choose the kind of legal matter you want to create</li>
              <li>
                Complete all the intake information required for your specific
                kind of legal matter
              </li>
              <li>
                Your provider law firm will contact you once the legal matter
                has been created
              </li>
            </ul>
          </Alert>
        )}
      </>
    )
  }

  return (
    <Box sx={{ paddingBottom: '24px' }}>
      {hasPastdueSubscription && (
        <>
          <Alert severity="warning" icon={false} sx={{ mb: 3 }} role="alert">
            <Typography variant="h5" gutterBottom>
              One or more of your subscriptions is past due.{' '}
              <Link
                href={routes.userSubscriptionStatus.path}
                sx={{ fontSize: '16px', cursor: 'pointer' }}
              >
                {`Please update payment method`}
              </Link>{' '}
              so the issue can be resolved!
            </Typography>
          </Alert>
        </>
      )}
      {!hasSubscription && (
        <>
          <Alert severity="warning" icon={false} sx={{ mb: 3 }} role="alert">
            <Typography variant="h5" gutterBottom>
              You must have an active legal plan to request legal services.{' '}
              <Link
                href={routes.checkoutManageProducts.path}
                sx={{ fontSize: '16px', cursor: 'pointer' }}
              >
                {`Get a legal plan`}
              </Link>{' '}
              and become a LegalFix member today!
            </Typography>
          </Alert>
        </>
      )}
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid>
          <LegalMatterFilters
            selectedStatus={filterName}
            statusList={statusMapper}
            usStates={usStates}
            preferredStates={preferredStates}
            disableFilters={['firms']}
            onChange={handleFilterUpdates}
          />
        </Grid>
        <Grid mb={2}>
          <Button
            endIcon={<AddCircleSharpIcon sx={{ color: 'white' }} />}
            onClick={() => {
              navigate(routes.legalServiceSelection.path)
            }}
            aria-label="Legal Matter Kinds Dropdown Button"
            data-testid="create-legal-matter-dropdown"
            disabled={!hasSubscription}
            variant="contained"
            color="primary"
            role="button"
          >
            Request Legal Service
          </Button>
        </Grid>
      </Grid>
      <Divider sx={{ mb: 2 }} />
      {data?.legalMatters?.edges?.length === 0 &&
        renderNoResultsMessage(filterName)}
      {data?.legalMatters?.edges?.map((legalMatter, idx: number) => {
        return (
          <LegalMatterListItem
            key={`legal-matter-item-${idx}`}
            data-testid={`legal-matter-item`}
            legalMatter={legalMatter.node}
            userTimeZone={user?.profile?.timeZone}
            legalMatterStatusLabels={SubscriberLegalMatterStatusLabels}
            onClickButton={() => {
              alert('I will assign an Attorney')
            }}
            onClickViewDetails={(legalMatter) => {
              navigate(
                routes.legalMatterDetail.param({
                  legalMatterKey: legalMatter.key,
                }),
              )
            }}
          />
        )
      })}
      {data != null && data.legalMatters.pageInfo.hasNextPage && (
        <Button
          loading={networkStatus === NetworkStatus.fetchMore}
          variant="contained"
          color="primary"
          aria-label="Load More"
          onClick={onClickLoadMore}
          disabled={!data.legalMatters.pageInfo.hasNextPage}
          data-testid="loading-button"
        >
          Load More
        </Button>
      )}
    </Box>
  )
}

export default SubscriberLegalMatterListing
